import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { formatMoney, formatPercent, type MoneyFormatOptions } from '../i18n/format';

/**
 * Formatting context for components: billing-region locale (numbers,
 * currency) and the budget's currency. Provided once by the app; a budget
 * screen can override the currency for its own subtree.
 */
export interface FormatSettings {
  formatLocale: string;
  currency: string;
}

const Ctx = createContext<FormatSettings>({ formatLocale: 'en-US', currency: 'USD' });

export function FormatProvider({ value, children }: { value: FormatSettings; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFormat() {
  const settings = useContext(Ctx);
  return useMemo(
    () => ({
      ...settings,
      money: (minor: number, opts?: MoneyFormatOptions & { currency?: string }) =>
        formatMoney(minor, opts?.currency ?? settings.currency, settings.formatLocale, opts),
      percent: (ratio: number, opts?: { signed?: boolean; digits?: number }) => formatPercent(ratio, settings.formatLocale, opts),
    }),
    [settings],
  );
}
